import React, { useMemo, useState } from 'react';
import classes from './LocationSection.module.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { resourceHelpers } from '@/utils/helpers';
import isEmpty from 'lodash/isEmpty';
import geometry from '@/utils/geometry';
import useLocation from '@/hooks/useLocation';
import ResourceMap from '@/features/googleMap/components/ResourceMap/ResourceMap';
import DistanceTo from '@/components/Resources/DistanceTo/DistanceTo';
import AutoComplete from '@/features/googleMap/components/GoogleLocation/AutoComplete';
import t from '@/utils/Language/OrganizationPage-translations';

const MAX_VISIBLE = 3;

const LocationBlock = ({ location, showDivider }) => {
  const schedule = isEmpty(location?.schedule) ? null : resourceHelpers.generateSchedule(location.schedule, false, true);
  const directionsUrl = 'https://maps.google.com?daddr=' + resourceHelpers.buildDirectionsUrl(location);
  const phones = location?.phones || [];

  return (
    <div className={`${classes.locationBlock} ${showDivider ? classes.locationBlockDivider : ''}`}>
      <div className={classes.locationBlockHeader}>
        <div className={classes.locationName}>{location.name}</div>
        <DistanceTo locationList={[location]} className={classes.locationDistance} />
      </div>
      <address className={classes.locationAddress}>
        {location.address}
        {location.unit && `, ${location.unit}`}
        <br />
        {location.city}, {location.state} {location.zip_code}
      </address>

      {schedule && <div className={classes.scheduleTable}>{schedule}</div>}

      {location.schedule?.notes && <p className={classes.scheduleNotes}>{location.schedule.notes}</p>}

      <div className={classes.locationFooter}>
        <a href={directionsUrl} target="_blank" rel="noopener noreferrer" className={classes.directionsLink}>
          <FontAwesomeIcon icon={['fas', 'location-dot']} className={classes.directionsIcon} />
          {t('Get directions')}
        </a>
        {phones.length > 0 && (
          <div className={classes.phoneList}>
            {phones.map((phone, i) => {
              const isFax = phone.phone_type?.toLowerCase() === 'fax';
              return (
                <span key={i} className={classes.phoneItem}>
                  <FontAwesomeIcon icon={['fas', isFax ? 'fax' : 'phone']} className={classes.phoneIcon} />
                  {isFax ? (
                    <span className={classes.faxDigits}>{phone.digits}</span>
                  ) : (
                    <a href={`tel:${phone.digits}`} className={classes.phoneLink}>
                      {phone.digits}
                    </a>
                  )}
                  {phone.phone_type && <span className={classes.phoneType}> ({phone.phone_type})</span>}
                </span>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const LocationSection = ({ resource }) => {
  const [showAll, setShowAll] = useState(false);
  const { locationContext } = useLocation();
  const locations = resource?.locations;

  // Mirrors the Angular resource page: locations are listed nearest-first relative to
  // the member's location. Server render (and hydration) keeps API order since the
  // member's location only resolves client-side, after the localStorage effect.
  const sortedLocations = useMemo(
    () => (locationContext.locationKnown?.() ? geometry.sortLocationsByDistance(locationContext.location, locations) : locations),
    [locations, locationContext.location]
  );

  const primaryLocation = sortedLocations && sortedLocations.length > 0 ? sortedLocations[0] : null;
  const resourceScheduleNotes = resource?.schedule?.notes || null;

  if (!locations || locations.length === 0) {
    return (
      <div className={classes.locationSectionContainer} id="org-location">
        <div className={classes.locationSectionPanel}>
          <div className={classes.locationSectionHeader}>
            <div className={classes.locationSectionTitleRow}>
              <FontAwesomeIcon icon={['fas', 'street-view']} className={classes.locationSectionIcon} />
              <h2 className={classes.locationSectionTitle}>{t('Locations')}</h2>
            </div>
          </div>
          <div className={classes.locationSectionContent}>
            {resource?.resource_type === 'Opportunity' ? (
              <div className={classes.noLocation}>
                <p className={classes.noLocationText}>
                  {t('This service does not have a location or its location is private.')}{' '}
                  {resource?.region && t('This program can be accessed in ${region}.', { region: resource.region })}
                </p>
              </div>
            ) : (
              <p className={classes.noLocation}>
                {t('This organization does not have a location or its location is private. This organization can be accessed in ${region}.', {
                  region: resource?.region
                })}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  const visibleLocations = showAll ? sortedLocations : sortedLocations.slice(0, MAX_VISIBLE);
  const hiddenCount = sortedLocations.length - MAX_VISIBLE;

  return (
    <div className={classes.locationSectionContainer} id="org-location">
      <div className={classes.locationSectionPanel}>
        <div className={classes.locationSectionHeader}>
          <div className={classes.locationSectionTitleRow}>
            <FontAwesomeIcon icon={['fas', 'street-view']} className={classes.locationSectionIcon} />
            <h2 className={classes.locationSectionTitle}>{t('Locations')}</h2>
          </div>
          {locations.length > 1 && (
            <div className={classes.locationSearchWrapper}>
              <FontAwesomeIcon icon={['fas', 'magnifying-glass']} className={classes.locationSearchIcon} />
              <AutoComplete
                elementId="location-section-search"
                placeholder={t('Find locations close to...')}
                additionalClasses={classes.locationSearchInput}
                locationObject={{}}
                defaultLocation=""
              />
            </div>
          )}
        </div>
        <div className={classes.locationSectionContent}>
          <div className={classes.mapContainer}>
            <ResourceMap
              options={{
                center: { lat: primaryLocation.lat, lng: primaryLocation.long },
                zoom: 14
              }}
              locations={sortedLocations}
            />
          </div>

          {visibleLocations.map((location, i) => (
            <LocationBlock key={location.id || i} location={location} showDivider={i > 0} />
          ))}

          {resourceScheduleNotes && <p className={classes.resourceScheduleNotes}>{resourceScheduleNotes}</p>}

          {!showAll && hiddenCount > 0 && (
            <button className={classes.showMoreBtn} onClick={() => setShowAll(true)}>
              {/* Single interpolated key so translators control word order. */}
              {t('Show ${count} more locations', { count: hiddenCount })}
              <FontAwesomeIcon icon={['fas', 'caret-down']} className={classes.showMoreIcon} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default LocationSection;
